import React, { useEffect, useState } from 'react';
import {
    MenuItem,
    FormControl,
    Select,
    Paper,
    TextField,
    Grid,
    Box,
    Typography,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Button, ErrorMessage } from '@biq/apex-ui';
import {
    ErrorObjType,
    IFormInputs,
    btnGridSize,
    fieldGridSize,
    initialValues,
    schema,
    styles,
    FilterMode,
} from './life-graph-filters-helpers';
import {
    Dictionary,
    DictionaryAttribute,
    BREAKPOINT_KEYS,
    useMinWidthMediaQueries,
    validationsMessages,
    themeObject,
} from 'lib';
import {
    AutocompleteChainSelector,
    FormLabel,
    InfoTooltipIcon,
} from 'components';
import { LifeGraphExplorerFilter } from '../lifegraph-explorer/lifegraph-explorer.types';

interface LifegraphFilterProps {
    setFilter: any;
    filter: LifeGraphExplorerFilter | undefined;
    dictionaries: Dictionary[];
    handleFilterModeChange: (mode: FilterMode, formData: IFormInputs) => void;
}

export const LifeGraphBasicFilter = (
    props: LifegraphFilterProps
): React.JSX.Element => {
    const { filter, setFilter, dictionaries, handleFilterModeChange } = props;
    const isMediaBreakpoint: boolean = useMinWidthMediaQueries(
        BREAKPOINT_KEYS.XXL
    );
    const [attributeKeyValue, setAttributeKeyValue] = useState<string>('None');
    const [whereSelectError, setWhereSelectError] = useState<ErrorObjType>({
        error: false,
        message: '',
    });
    const [whereTextFieldError, setWhereTextFieldError] =
        useState<ErrorObjType>({
            error: false,
            message: '',
        });

    const {
        handleSubmit,
        control,
        register,
        watch,
        getValues,
        reset,
        setValue,
        formState: { errors },
    } = useForm<IFormInputs>({
        mode: 'all',
        resolver: yupResolver(schema),
        defaultValues: filter,
    });

    const chain = watch('chain');

    useEffect(() => {
        if (chain) {
            setAttributeKeyValue('None');
            reset({
                attributeKey: 'None',
                attributeValue: '',
                chain: chain,
                limit: 1,
                depth: 1,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chain]);

    const onSubmit = (formInputs: IFormInputs): void => {
        setWhereSelectError({
            error: false,
            message: '',
        });
        setWhereTextFieldError({
            error: false,
            message: '',
        });
        if (
            formInputs.attributeKey &&
            formInputs.attributeKey !== 'None' &&
            !formInputs.attributeValue
        ) {
            setWhereTextFieldError({
                error: true,
                message: validationsMessages.required,
            });
            return;
        }
        if (
            formInputs.attributeValue &&
            (!formInputs.attributeKey || formInputs.attributeKey === 'None')
        ) {
            setWhereSelectError({
                error: true,
                message: validationsMessages.required,
            });
            return;
        }
        setFilter({ ...formInputs });
    };

    const generateAttributeList = (options: Dictionary[]): React.ReactNode => {
        const selectedDictionary: Dictionary | undefined = options.find(
            (dict) => dict.name === chain
        );

        if (selectedDictionary) {
            const updatedAttributes = selectedDictionary.attributes ?? [];
            updatedAttributes.sort((a: any, b: any) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });

            return updatedAttributes.map((val: DictionaryAttribute) => (
                <MenuItem key={val?.name} value={val?.name}>
                    {val?.name}
                </MenuItem>
            ));
        }
    };

    return (
        <Paper sx={styles.paper}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                    container
                    justifyContent='space-between'
                    alignItems='flex-start'
                >
                    <Grid item xs={fieldGridSize}>
                        <FormControl sx={styles.formControl}>
                            <Grid sx={styles.fieldContainerStyles}>
                                <Grid position={'relative'} top={'6px'}>
                                    <FormLabel
                                        required
                                        text='Chain'
                                        condensed
                                    />
                                    :
                                </Grid>
                                <Grid>
                                    <AutocompleteChainSelector
                                        tooltipTitle={chain}
                                        controlName='chain'
                                        selectedChain={chain}
                                        control={control}
                                        error={!!errors.chain}
                                        errorText={errors.chain?.message ?? ''}
                                        autocompleteStyles={styles.chainSelect}
                                    />
                                </Grid>
                            </Grid>
                        </FormControl>

                        <FormControl
                            sx={{
                                ...styles.formControl,
                                ...styles.containerStyles,
                            }}
                        >
                            <Grid sx={styles.containerFlexStyles}>
                                <Grid
                                    sx={styles.whereFieldLabelContainerStyles}
                                >
                                    <FormLabel text='Where:' condensed />
                                    <InfoTooltipIcon
                                        title={
                                            <div>
                                                <Typography
                                                    sx={{
                                                        ...styles.tooltipText,
                                                        ...themeObject
                                                            .typography.captions
                                                            .bold,
                                                    }}
                                                >
                                                    Searching for strings? Use
                                                    single quotes.
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        ...styles.tooltipText,
                                                        ...themeObject
                                                            .typography.captions
                                                            .regular,
                                                    }}
                                                >
                                                    &#8226; &#39;Laura&#39;
                                                    returns the string
                                                    <br />
                                                    &#8226; 5 returns the value
                                                    <br />
                                                    &#8226; &#39;5&#39; returns
                                                    the string
                                                </Typography>
                                            </div>
                                        }
                                        popperStyles={styles.popperStyles}
                                    />
                                </Grid>
                                <Grid>
                                    <Select
                                        id='simple-select-attributeKey'
                                        {...register('attributeKey')}
                                        value={attributeKeyValue}
                                        sx={{
                                            ...styles.select,
                                            ...styles.whereSelect,
                                        }}
                                        onChange={(e: any): void => {
                                            setAttributeKeyValue(
                                                e.target.value
                                            );
                                            setWhereSelectError({
                                                error: false,
                                                message: '',
                                            });
                                        }}
                                        error={whereSelectError.error}
                                        MenuProps={{
                                            sx: styles.selectFieldHeight,
                                            anchorOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                        }}
                                    >
                                        <MenuItem key={'None'} value={'None'}>
                                            None
                                        </MenuItem>
                                        {generateAttributeList(dictionaries)}
                                    </Select>
                                    {whereSelectError?.error && (
                                        <Box sx={styles.errorContainerStyles}>
                                            <ErrorMessage
                                                errorMessage={
                                                    whereSelectError.message
                                                }
                                            />
                                        </Box>
                                    )}
                                </Grid>
                                <FormLabel text='==' />
                                <Grid>
                                    <TextField
                                        className='attribute-value-field'
                                        defaultValue={
                                            initialValues?.attributeValue
                                        }
                                        size='small'
                                        id='simple-select-attributeValue'
                                        {...register('attributeValue')}
                                        onChange={(e: any): void => {
                                            setValue(
                                                'attributeValue',
                                                e.target.value
                                            );
                                            setWhereTextFieldError({
                                                error: false,
                                                message: '',
                                            });
                                        }}
                                        sx={styles.input}
                                        error={
                                            whereTextFieldError.error ||
                                            !!errors.attributeValue
                                        }
                                    />
                                    {(whereTextFieldError?.error ||
                                        errors.attributeValue) && (
                                        <Box sx={styles.errorContainerStyles}>
                                            <ErrorMessage
                                                errorMessage={
                                                    (whereTextFieldError.message ||
                                                        errors?.attributeValue
                                                            ?.message) ??
                                                    ''
                                                }
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </FormControl>

                        <FormControl
                            sx={{
                                ...styles.formControl,
                                ...styles.limitAndDepthContainerStyles,
                            }}
                        >
                            <Grid container>
                                <Grid
                                    sx={
                                        styles.limitAndDepthFieldLabelContainerStyles
                                    }
                                >
                                    <FormLabel
                                        required
                                        text='Limit'
                                        condensed
                                    />
                                    :
                                </Grid>
                                <Grid>
                                    <TextField
                                        defaultValue={initialValues?.limit}
                                        id='simple-select-limit'
                                        {...register('limit')}
                                        type='number'
                                        sx={styles.limit}
                                        error={!!errors?.limit}
                                    />
                                    {errors?.limit && (
                                        <Box sx={styles.errorContainerStyles}>
                                            <ErrorMessage
                                                errorMessage={
                                                    errors?.limit?.message ?? ''
                                                }
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </FormControl>

                        <FormControl
                            sx={{
                                ...styles.formControl,
                                ...styles.limitAndDepthContainerStyles,
                            }}
                        >
                            <Grid container>
                                <Grid
                                    sx={
                                        styles.limitAndDepthFieldLabelContainerStyles
                                    }
                                >
                                    <FormLabel
                                        required
                                        text='Depth'
                                        condensed
                                    />
                                    :
                                </Grid>
                                <Grid>
                                    <TextField
                                        id='simple-select-depth'
                                        defaultValue={1}
                                        {...register('depth')}
                                        type='number'
                                        sx={styles.limit}
                                        error={!!errors?.depth}
                                    />
                                    {errors?.depth && (
                                        <Box sx={styles.errorContainerStyles}>
                                            <ErrorMessage
                                                errorMessage={
                                                    errors?.depth?.message ?? ''
                                                }
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </FormControl>
                    </Grid>

                    <Grid
                        item
                        xs={btnGridSize}
                        sx={{
                            ...styles.buttonContainer,
                            ...(isMediaBreakpoint
                                ? styles.expandedButtonContainer
                                : styles.minimizedButtonContainer),
                        }}
                    >
                        <Button
                            sx={styles.applyButton}
                            variant='contained'
                            type='submit'
                            size='small'
                            color='primary'
                        >
                            Apply
                        </Button>
                        <Button
                            sx={styles.advancedButton}
                            variant='outlined'
                            size='small'
                            onClick={(): void => {
                                let formData = getValues();
                                handleFilterModeChange('Advanced', formData);
                            }}
                        >
                            Advanced Filters
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Paper>
    );
};
